import { InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ErrorHandler, onError } from '@apollo/client/link/error';
import { ApolloClient, HttpLink, from, split } from '@apollo/client';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';

export type ApolloClientOptions = {
  // GraphQL endpoint URI
  uri: string;
  // Optional custom Apollo cache instance
  cache?: InMemoryCache;
  // Optional function to get a fresh auth token
  getToken?: () => Promise<{ token: string; tokenType: string }>;
  // Optional custom error handler
  onError?: ErrorHandler;
};

export const createApolloClient = (options: ApolloClientOptions) => {
  const {
    uri,
    cache = new InMemoryCache(globalThis.__APOLLO_STATE__),
    getToken,
    onError: customOnError = () => {},
  } = options;

  const getAuthorization = async () => {
    if (!getToken) return {};
    const { token, tokenType = 'Bearer' } = await getToken();
    return { authorization: `${tokenType} ${token}` };
  };

  const authLink = setContext(async (_, { headers }) => {
    return {
      headers: {
        ...headers,
        ...(await getAuthorization()),
      },
    };
  });

  const errorLink = onError(customOnError);

  const httpLink = new HttpLink({
    uri,
    credentials: getToken ? 'include' : 'same-origin',
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: uri.replace(/^http/, 'ws'),
      lazy: true,
      retryAttempts: Infinity,
      retryWait: async (retries) => {
        const waitTime = Math.min(1000 * 2 ** retries, 30_000);
        await new Promise<void>((resolve) => setTimeout(resolve, waitTime));
      },
      shouldRetry: (errOrCloseEvent) => {
        console.log('ws shouldRetry', errOrCloseEvent);
        const code = (errOrCloseEvent as CloseEvent)?.code;
        return !(code === 1000 || code === 4401 || code === 4403);
      },
      connectionParams: async () => ({
        ...(await getAuthorization()),
      }),
      on: {
        connected: () => console.log('[ws] connected'),
        closed: (e) => console.warn('[ws] closed', e),
        error: (e) => console.error('[ws] error', e),
      },
    }),
  );

  const splitLink = split(
    ({ query }) => {
      const def = getMainDefinition(query);
      return def.kind === 'OperationDefinition' && def.operation === 'subscription';
    },
    wsLink,
    httpLink,
  );

  const graphqlClient = new ApolloClient({
    cache,
    link: from([errorLink, authLink, splitLink]),
  });

  return graphqlClient;
};
